/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import { modalActions } from "modules/modal";
import { purchaseActions } from "modules/purchase";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { getCardTokenGMO } from "utility/gmo";

import type { CardInfo } from "modules/purchase";

type Props = {
  isDarkModeEnabled: boolean;
  selectedCreditCardSeq: string | null;
  selectedSecurityCode: string | null;
  setSelectedCreditCardSeq: (cardSeq: string | null) => void;
  setSelectedSecurityCode: (code: string | null) => void;
};

type CardInfoWithoutSeq = Omit<CardInfo, "cardSeq">;

export const StreamingLiveCommerceCreditPaymentV2: React.FC<Props> = ({
  isDarkModeEnabled,
  selectedCreditCardSeq,
  selectedSecurityCode,
  setSelectedCreditCardSeq,
  setSelectedSecurityCode,
}) => {
  const registeredCardList = useSelector(
    (state) => state.purchase.cardInfoList
  );
  const [selectedCardType, setSelectedCardType] = useState<
    "registered" | "new"
  >("new");
  const [inputCardInfo, setInputCardInfo] = useState<CardInfoWithoutSeq>({
    cardNumber: "",
    cardExpireMonth: "",
    cardExpireYear: "",
    cardHolderName: "",
    cardSecurityCode: "",
  });

  const dispatch = useDispatch();

  //カード情報を登録
  const handleRegisterNewCard = async (
    registerCardInfo: CardInfoWithoutSeq
  ) => {
    try {
      const response = await getCardTokenGMO({
        cardNo: registerCardInfo.cardNumber,
        expire:
          registerCardInfo.cardExpireYear.toString() +
          registerCardInfo.cardExpireMonth.toString(),
        securityCode: registerCardInfo.cardSecurityCode ?? "",
        holderName: registerCardInfo.cardHolderName,
        tokenNumber: 1,
      });

      if (!response.tokens || response.tokens.length === 0) {
        dispatch(modalActions.toggleError({ msg: "カード登録に失敗しました" }));
        return;
      }

      dispatch(
        purchaseActions.registerCardInfo.started({ token: response.tokens[0] })
      );
      setSelectedCardType("registered");
    } catch (error) {
      dispatch(
        modalActions.toggleError({ msg: "システムエラーが発生しました" })
      );
    }
  };

  const handleChangeCardInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    //TODO: バリデーションを追加する
    const { name, value } = e.target;
    setInputCardInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeSelectedCardSeq = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedCardSeq = e.target.value;
    setSelectedCreditCardSeq(selectedCardSeq);
    setSelectedSecurityCode(null);
  };

  const classesCreditPaymentDesign = creditPaymentDesign({ isDarkModeEnabled });
  const { t } = useI18n();

  return (
    <div css={classesCreditPaymentDesign.container}>
      <p css={classesCreditPaymentDesign.subLabel}>
        {t("streaming.commerceCart.cardSelect")}
      </p>

      <div css={classesCreditPaymentDesign.selectionBox}>
        <input
          type="radio"
          id="card-registered"
          name="cardSelection"
          checked={selectedCardType === "registered"}
          onChange={() => setSelectedCardType("registered")}
          css={classesCreditPaymentDesign.radioInput}
        />
        <label
          htmlFor="card-registered"
          css={classesCreditPaymentDesign.radioLabel}
        >
          {t("streaming.commerceCart.registeredCard")}
        </label>
      </div>

      {selectedCardType === "registered" && registeredCardList && (
        <div css={classesCreditPaymentDesign.formContainer}>
          <div css={classesCreditPaymentDesign.inputGroup}>
            {registeredCardList.length > 0 ? (
              <React.Fragment>
                <select
                  css={classesCreditPaymentDesign.inputSelect}
                  onChange={handleChangeSelectedCardSeq}
                  value={selectedCreditCardSeq ?? ""}
                >
                  {registeredCardList.map((card) => (
                    <option value={card.cardSeq} key={card.cardSeq}>
                      **** **** **** {card.cardNumber.slice(-4)}
                    </option>
                  ))}
                </select>
                {/* セキュリティコード */}
                <div
                  css={classesCreditPaymentDesign.inputGroup}
                  style={{ marginTop: "16px" }}
                >
                  <label>{t("streaming.commerceCart.cardCVV")}</label>
                  <input
                    type="text"
                    name="cardSecurityCode"
                    placeholder="123"
                    css={[
                      classesCreditPaymentDesign.textInput,
                      css`
                        width: 80px;
                      `,
                    ]}
                    onChange={(e) => setSelectedSecurityCode(e.target.value)}
                    value={selectedSecurityCode ?? ""}
                  />
                </div>
              </React.Fragment>
            ) : (
              <p>{t("streaming.commerceCart.noRegisteredCard")}</p>
            )}
          </div>
        </div>
      )}

      <div css={classesCreditPaymentDesign.selectionBox}>
        <input
          type="radio"
          id="card-new"
          name="cardSelection"
          checked={selectedCardType === "new"}
          onChange={() => setSelectedCardType("new")}
          css={classesCreditPaymentDesign.radioInput}
        />
        <label htmlFor="card-new" css={classesCreditPaymentDesign.radioLabel}>
          {t("streaming.commerceCart.newCard")}
        </label>
      </div>

      {selectedCardType === "new" && (
        <div css={classesCreditPaymentDesign.formContainer}>
          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardNumber")}</label>
            <input
              type="text"
              placeholder="1234123412341234"
              name="cardNumber"
              css={classesCreditPaymentDesign.textInput}
              onChange={handleChangeCardInput}
            />
            <div css={classesCreditPaymentDesign.brandImageWrapper}>
              <img
                src="/images/liveCommerce/paymentMethodCard.png"
                alt="Card Brands"
                css={classesCreditPaymentDesign.brandImage}
              />
            </div>
          </div>

          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardExpiration")}</label>
            <div css={classesCreditPaymentDesign.flexRow}>
              <input
                type="text"
                name="cardExpireMonth"
                placeholder="MM"
                css={classesCreditPaymentDesign.smallInput}
                onChange={handleChangeCardInput}
              />
              <input
                type="text"
                name="cardExpireYear"
                placeholder="YY"
                css={classesCreditPaymentDesign.smallInput}
                onChange={handleChangeCardInput}
              />
            </div>
          </div>

          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardHolderName")}</label>
            <input
              type="text"
              name="cardHolderName"
              placeholder="TARO YAMADA"
              css={classesCreditPaymentDesign.textInput}
              onChange={handleChangeCardInput}
            />
          </div>

          <div css={classesCreditPaymentDesign.inputGroup}>
            <label>{t("streaming.commerceCart.cardCVV")}</label>
            <input
              type="text"
              name="cardSecurityCode"
              placeholder="123"
              css={[
                classesCreditPaymentDesign.textInput,
                css`
                  width: 80px;
                `,
              ]}
              onChange={handleChangeCardInput}
            />
          </div>
          <div
            onClick={async () => handleRegisterNewCard(inputCardInfo)}
            css={classesCreditPaymentDesign.registerCardButton}
          >
            {t("streaming.commerceCart.register")}
          </div>
        </div>
      )}
    </div>
  );
};

type CreditPaymentStylesProps = {
  isDarkModeEnabled: boolean;
};

const creditPaymentDesign = (props: CreditPaymentStylesProps) => {
  const theme = {
    text: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
    bg: props.isDarkModeEnabled ? "#000000" : "#FFFFFF",
    inputBg: props.isDarkModeEnabled ? "#1A1A1A" : "#F0F0F0",
    border: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
  };

  const container = css`
    padding: 16px 0;
    margin: 0 16px;
    border-top: 1px solid ${theme.border};
  `;
  const subLabel = css`
    color: ${theme.text};
    margin-bottom: 12px;
  `;
  const selectionBox = css`
    margin-bottom: 8px;
  `;
  const radioInput = css`
    display: none;
    &:checked + label::before {
      background-color: ${theme.text};
    }
    &:checked + label::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 29px;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background-color: ${theme.bg};
      border-radius: 50%;
    }
  `;
  const radioLabel = css`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 16px 16px 16px 56px;
    background-color: ${theme.bg};
    color: ${theme.text};
    font-size: 14px;
    cursor: pointer;
    box-sizing: border-box;
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      border: 2px solid ${theme.border};
      border-radius: 50%;
      box-sizing: border-box;
    }
  `;
  const formContainer = css`
    margin-top: 20px;
    padding-left: 20px;
  `;
  const inputGroup = css`
    margin-bottom: 16px;
    label {
      display: block;
      font-size: 14px;
      margin-bottom: 8px;
      color: ${theme.text};
    }
  `;
  const textInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    border: none;
    color: ${theme.text};
    font-size: 16px;
    &::placeholder {
      color: #a3a3a3;
    }
  `;

  const inputSelect = css`
    appearance: none;
    background-color: ${theme.bg};
    padding: 0px 16px;
    border-radius: 10px;
    height: 40px;
    border: none;
    color: ${theme.text};
    font-size: 16px;
    width: 100%;
  `;
  const flexRow = css`
    display: flex;
    gap: 12px;
  `;
  const smallInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    font-size: 16px;
    width: 70px;
    color: ${theme.text};
    &::placeholder {
      color: #a3a3a3;
    }
  `;
  const brandImageWrapper = css`
    margin-top: 8px;
  `;
  const brandImage = css`
    height: 24px;
    object-fit: contain;
  `;

  const registerCardButton = css`
    margin-top: 16px;
    color: ${theme.text};
    background-color: ${theme.bg};
    font-size: 14px;
    cursor: pointer;
    width: fit-content;
    border-radius: 4px;
    padding: 4px 8px;
  `;

  return {
    container,
    subLabel,
    selectionBox,
    radioInput,
    radioLabel,
    formContainer,
    inputGroup,
    textInput,
    inputSelect,
    flexRow,
    smallInput,
    brandImageWrapper,
    brandImage,
    registerCardButton,
  };
};
